import { makeStyles, tokens } from '@fluentui/react-components';
import { mq, fontScale } from '@/theme/scalingTokens';

export const useBookingsScreenStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    overflow: 'hidden',
  },
  /**
   * Single scroll container shared by the HeroBanner, the sticky StepIndicator
   * wrapper, and the BookingForm content. The banner can scroll upwards while
   * the stepper pins below the global header (spec 101 US2 — revised).
   * Carries no horizontal padding so the banner retains its own native
   * margins; `formContent` handles the form area's padding + centering.
   */
  scrollArea: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  /**
   * Wrapper for the BookingForm content area. Inherits the prior
   * `formContainer` horizontal padding, max-width, and centering so the form
   * content layout is unchanged.
   */
  formContent: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '96px',
    paddingRight: '96px',
    maxWidth: '1326px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
    // Horizontal paddings mirror HeroBanner's outer margins at every
    // breakpoint so the form card sits flush with the banner above it.
    // Smaller max-widths come AFTER larger ones so the more specific
    // (narrower) media query wins via source order.
    [mq.ultraWideUp]: {
      maxWidth: 'none',
      paddingLeft: '5%',
      paddingRight: '5%',
    },
    [mq.wide]: {
      paddingLeft: '48px',
      paddingRight: '48px',
    },
    [mq.smallDesktop]: {
      paddingLeft: '24px',
      paddingRight: '24px',
      paddingTop: '16px',
      paddingBottom: '16px',
    },
    [mq.tablet]: {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
    [mq.mobile]: {
      paddingLeft: '12px',
      paddingRight: '12px',
      paddingTop: '12px',
      paddingBottom: '12px',
    },
  },
  loadingContent: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '100%',
    boxSizing: 'border-box',
  },
  /**
   * Sticky StepIndicator wrapper rendered as a sibling of HeroBanner inside
   * `scrollArea`. Margins mirror the banner's outer margins and the inner
   * padding mirrors HeroBanner.figmaInner's horizontal padding so the
   * StepIndicator pill keeps the exact same horizontal position and width
   * whether the page is scrolled (sticky, pinned below header) or not
   * (in-flow, sitting on the banner's bottom edge). A negative top margin
   * pulls the wrapper UP so the pill visually overlaps onto the banner card
   * in the default no-scroll state — matching the original "stepper on the
   * banner" look. The pill itself already has its own opaque background +
   * shadow in the figma variant, so it reads as a floating element on top
   * of the banner.
   * Sticky disabled on mobile (≤480px) to preserve viewport height.
   */
  stickyStepperWrapper: {
    position: 'sticky',
    top: tokens.spacingVerticalM,
    zIndex: 2,
    display: 'flex',
    justifyContent: 'center',
    boxSizing: 'border-box',
    marginLeft: '48px',
    marginRight: '48px',
    // Negative marginTop ≈ half the figma StepIndicator pill height (~2.5rem
    // tall: 1.5rem step circle + 2 × 0.5rem spacingVerticalS padding). Pulls
    // the wrapper up so the banner's bottom border line bisects the pill —
    // the pill sits half above / half below the banner edge, like a tab
    // anchored to the banner.
    marginTop: '-1.25rem',
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    // Width compensates for outer margins so the wrapper spans the same
    // horizontal area as the HeroBanner card without overflowing.
    width: 'calc(100% - 96px)',
    [mq.smallDesktop]: {
      marginLeft: '24px',
      marginRight: '24px',
      width: 'calc(100% - 48px)',
    },
    [mq.tablet]: {
      marginLeft: '16px',
      marginRight: '16px',
      width: 'calc(100% - 32px)',
      paddingLeft: tokens.spacingHorizontalL,
      paddingRight: tokens.spacingHorizontalL,
    },
    [mq.mobile]: {
      position: 'static',
      marginLeft: '12px',
      marginRight: '12px',
      width: 'calc(100% - 24px)',
    },
  },
  heroBackLink: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    fontSize: fontScale.subtitle,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForegroundLink,
    textDecoration: 'none',
    ':hover': { textDecoration: 'underline' },
  },
});
